<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Relatórios</h1>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="-mb-px flex space-x-8">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="$router.push(tab.key === 'sales' ? '/admin/reports/sales' : '/admin/reports/deliveries')"
          class="py-4 px-1 border-b-2 font-medium text-sm"
          :class="activeTab === tab.key ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
        >
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <!-- Relatório de Vendas -->
    <div v-if="activeTab === 'sales'" class="space-y-6">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Período:</label>
          <select
            v-model="salesPeriod"
            @change="loadSalesReport"
            class="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="month">Mês atual</option>
            <option value="week">Semana atual</option>
            <option value="day">Dia atual</option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Status:</label>
          <select
            v-model="salesStatus"
            @change="loadSalesReport"
            class="border rounded-lg px-3 py-2 text-sm"
          >
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p class="text-sm text-gray-500">Total de Vendas</p>
          <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(salesReport.total_sales) }}</p>
        </div>
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p class="text-sm text-gray-500">Total de Pedidos</p>
          <p class="text-2xl font-bold text-gray-900">{{ salesReport.total_orders }}</p>
        </div>
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p class="text-sm text-gray-500">Ticket Médio</p>
          <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(salesReport.average_ticket) }}</p>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="order in salesReport.orders" :key="order.id">
              <td class="px-6 py-3 text-sm font-medium text-gray-900">#{{ order.order_number }}</td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ order.customer_name || 'N/A' }}</td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ formatDateTime(order.created_at) }}</td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ getStatusLabel(order.status) }}</td>
              <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ formatCurrency(order.total) }}</td>
            </tr>
            <tr v-if="salesReport.orders.length === 0">
              <td colspan="5" class="px-6 py-8 text-center text-gray-500">Nenhum pedido encontrado no período</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Relatório de Entregas -->
    <div v-if="activeTab === 'deliveries'" class="space-y-6">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Agrupar por:</label>
          <select
            v-model="deliveriesGroupBy"
            @change="loadDeliveriesReport"
            class="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="driver">Entregador</option>
            <option value="day">Dia</option>
            <option value="month">Mês</option>
          </select>
        </div>
        <div v-if="deliveriesGroupBy === 'driver'" class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">Período:</label>
          <select
            v-model="deliveriesPeriod"
            @change="loadDeliveriesReport"
            class="border rounded-lg px-3 py-2 text-sm"
          >
            <option v-for="option in deliveryPeriodOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {{ deliveriesGroupBy === 'driver' ? 'Entregador' : deliveriesGroupBy === 'day' ? 'Dia' : 'Mês' }}
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de Entregas</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(row, index) in deliveriesReport.data" :key="index">
              <td class="px-6 py-3 text-sm font-medium text-gray-900">
                {{ deliveriesGroupBy === 'driver' ? row.driver_name : deliveriesGroupBy === 'day' ? row.date : row.month }}
              </td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ row.total_deliveries }}</td>
              <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ formatCurrency(row.total_value) }}</td>
            </tr>
            <tr v-if="deliveriesReport.data.length === 0">
              <td colspan="3" class="px-6 py-8 text-center text-gray-500">Nenhuma entrega encontrada</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { reportApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError } from '@/utils/notifications'

interface SalesOrder {
  id: number
  order_number: string
  total: number
  status: string
  created_at: string
  customer_name: string
}

interface SalesReport {
  total_sales: number
  total_orders: number
  average_ticket: number
  orders: SalesOrder[]
}

interface DeliveryRow {
  driver_name?: string
  date?: string
  month?: string
  total_deliveries: number
  total_value: number
}

interface DeliveriesReport {
  data: DeliveryRow[]
}

const route = useRoute()

const tabs = [
  { key: 'sales', label: 'Vendas' },
  { key: 'deliveries', label: 'Entregas' }
]

const activeTab = ref(route.path.includes('deliveries') ? 'deliveries' : 'sales')

watch(() => route.path, (newPath) => {
  activeTab.value = newPath.includes('deliveries') ? 'deliveries' : 'sales'
})
const salesPeriod = ref('month')
const salesStatus = ref('delivered')
const deliveriesGroupBy = ref('driver')
const deliveriesPeriod = ref('month')

const deliveryPeriodOptions = [
  { value: 'month', label: 'Mês atual' },
  { value: 'day', label: 'Dia atual' },
  { value: 'all', label: 'Todos os meses' }
]

const statusOptions = [
  { value: 'delivered', label: 'Entregue' },
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'preparing', label: 'Em Preparo' },
  { value: 'ready', label: 'Pronto' },
  { value: 'out_for_delivery', label: 'Em Entrega' },
  { value: 'cancelled', label: 'Cancelado' }
]

const salesReport = ref<SalesReport>({
  total_sales: 0,
  total_orders: 0,
  average_ticket: 0,
  orders: []
})

const deliveriesReport = ref<DeliveriesReport>({
  data: []
})

const formatDateTime = (value: string | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    preparing: 'Em Preparo',
    ready: 'Pronto',
    out_for_delivery: 'Em Entrega',
    delivered: 'Entregue',
    cancelled: 'Cancelado'
  }
  return labels[status] || status
}

const loadSalesReport = async () => {
  try {
    const res = await reportApi.getSales(salesPeriod.value, salesStatus.value)
    salesReport.value = res.data
  } catch (error) {
    console.error('Erro ao carregar relatório de vendas:', error)
    notifyError('Erro ao carregar relatório de vendas')
  }
}

const loadDeliveriesReport = async () => {
  try {
    const period = deliveriesGroupBy.value === 'driver' ? deliveriesPeriod.value : 'all'
    const res = await reportApi.getDeliveries(deliveriesGroupBy.value, period)
    deliveriesReport.value = res.data
  } catch (error) {
    console.error('Erro ao carregar relatório de entregas:', error)
    notifyError('Erro ao carregar relatório de entregas')
  }
}

onMounted(() => {
  loadSalesReport()
  loadDeliveriesReport()
})
</script>
